import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'

export const TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME = 'TELEGRAM_ACCOUNT'
export const MAX_ACCOUNT_FIELD_UNIQUE_NAME = 'MAX_ACCOUNT'

export const MESSENGER_IDENTITY_FIELD_UNIQUE_NAMES = [
  'VK_LINK',
  TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME,
  MAX_ACCOUNT_FIELD_UNIQUE_NAME,
] as const

export type MessengerIdentityFieldUniqueName =
  (typeof MESSENGER_IDENTITY_FIELD_UNIQUE_NAMES)[number]

export const TELEGRAM_ACCOUNT_VALUE_REGEX =
  /^(?:-?[0-9]+|https:\/\/t\.me\/[A-Za-z0-9_]+)$/

export const MAX_ACCOUNT_VALUE_REGEX =
  /^(?:-?[0-9]+|https:\/\/max\.ru\/u\/[A-Za-z0-9_-]+)$/

export const isMessengerIdentityFieldUniqueName = (
  uniqueName?: string | null,
): uniqueName is MessengerIdentityFieldUniqueName =>
  typeof uniqueName === 'string' &&
  (MESSENGER_IDENTITY_FIELD_UNIQUE_NAMES as readonly string[]).includes(
    uniqueName,
  )

const trimMessengerAccountValue = (value: string | null | undefined): string =>
  typeof value === 'string' ? value.trim() : ''

export const isValidTelegramAccountValue = (value: string): boolean => {
  const trimmed = trimMessengerAccountValue(value)
  if (!trimmed) {
    return true
  }

  return TELEGRAM_ACCOUNT_VALUE_REGEX.test(trimmed)
}

export const isValidMaxAccountValue = (value: string): boolean => {
  const trimmed = trimMessengerAccountValue(value)
  if (!trimmed) {
    return true
  }

  return MAX_ACCOUNT_VALUE_REGEX.test(trimmed)
}

export const mapMessengerIdentityFieldSaveErrors = (
  messages: string[],
): Partial<Record<MessengerIdentityFieldUniqueName, string>> => {
  const errors: Partial<Record<MessengerIdentityFieldUniqueName, string>> = {}

  messages.forEach((message) => {
    const text = String(message ?? '').trim()
    if (!text) {
      return
    }

    if (/telegram|t\.me/i.test(text)) {
      errors.TELEGRAM_ACCOUNT = text
      return
    }

    if (/\bmax\b|max\.ru/i.test(text)) {
      errors.MAX_ACCOUNT = text
      return
    }

    if (/вконтакт|vk\.com|\bvk\b/i.test(text)) {
      errors.VK_LINK = text
    }
  })

  return errors
}

export const extractCrmLeadFieldSaveErrorMessages = (error: unknown): string[] => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data

  if (!responseData || typeof responseData !== 'object') {
    return []
  }

  const message = (responseData as { message?: unknown }).message
  if (Array.isArray(message)) {
    return message.map((item) => String(item ?? '').trim()).filter(Boolean)
  }

  if (typeof message === 'string' && message.trim()) {
    return [message.trim()]
  }

  const responseError = (responseData as { error?: unknown }).error
  if (Array.isArray(responseError)) {
    return responseError.map((item) => String(item ?? '').trim()).filter(Boolean)
  }

  if (typeof responseError === 'string' && responseError.trim()) {
    return [responseError.trim()]
  }

  return []
}

/** Ошибка синхронизации чата из-за неоднозначного канала / отсутствия binding. */
export const isCrmLeadFieldBindingSyncError = (
  messages: string[] | null | undefined,
): boolean => {
  if (!Array.isArray(messages) || messages.length === 0) return false
  return messages.some((message) => {
    const text = String(message ?? '')
    return (
      /несколько активных каналов/i.test(text) ||
      /привяжите\s+crm-поле/i.test(text) ||
      /settings\.lead_field_id/i.test(text) ||
      (/невозможно автоматически создать чат/i.test(text) &&
        /wazzup|канал/i.test(text))
    )
  })
}

export const CRM_INTEGRATION_LEAD_FIELD_BINDING_SETTINGS_PATH =
  '/crm-settings?tab=6'

export const appendCrmLeadFieldBindingSettingsHint = (
  messages: string[],
): string[] => {
  if (!isCrmLeadFieldBindingSyncError(messages)) {
    return messages
  }

  const hint =
    `Настройте привязку поля к каналу: CRM → Общие/Интеграции (${CRM_INTEGRATION_LEAD_FIELD_BINDING_SETTINGS_PATH}).`

  if (messages.some((item) => item.includes(CRM_INTEGRATION_LEAD_FIELD_BINDING_SETTINGS_PATH))) {
    return messages
  }

  return [...messages, hint]
}

export const getMessengerAccountProviderByUniqueName = (
  uniqueName: MessengerIdentityFieldUniqueName,
): CrmChatProviderKey | null => {
  if (uniqueName === TELEGRAM_ACCOUNT_FIELD_UNIQUE_NAME) {
    return 'telegram'
  }

  if (uniqueName === MAX_ACCOUNT_FIELD_UNIQUE_NAME) {
    return 'max'
  }

  return null
}
